import { TraceType } from '../../common/enums/TraceType';
import { BaseGQLService } from '../../common/services/BaseGQLService';
import { ModelType } from '../../common/utils/GQLIdEncoder';
import { SlotMapper } from '../mappers/SlotMapper';
import { Slot } from '../models/Slot';
import { SlotType } from '../models/SlotType';

const SLOT_FIELDS = `
id
name
description
traceType
tags
project {
  maxItemCount
}
reports {
  totalCount
}
traces {
  totalCount
}
`;

export const GET_SLOT_QUERY = `
query($slotId: ID!) {
  slot: node(id: $slotId) {
    ... on Slot {
      ${SLOT_FIELDS}
    }
  }
}
`;

export const CREATE_SLOT_QUERY = `
mutation($projectId: ID!, $traceType: TraceType!, $name: String!, $description: String, $tags: [String]) {
  createSlot(
    input: {
      projectId: $projectId,
      name: $name,
      traceType: $traceType,
      description: $description
      tags: $tags
    }
  ) {
    slot {
      ${SLOT_FIELDS}
    }
  }
}
`;

export const UPDATE_SLOT_QUERY = `
mutation($slotId: ID!, $name: String, $description: String, $tags: [String]) {
  updateSlot(
    input: {
      slotId: $slotId,
      name: $name,
      description: $description,
      tags: $tags
    }
  ) {
    slot {
      ${SLOT_FIELDS}
    }
  }
}
`;

export const GET_SLOT_TYPES = `
query {
  traceTypes
  viewer {
    organization {
      allowedTraceTypes
    }
  }
}
`;

export const GET_PROJECT_SLOTS_QUERY = `
query($projectId: ID!) {
  node(id: $projectId) {
    ... on Project {
      slots {
        edges {
          node {
            ${SLOT_FIELDS}
          }
        }
      }
    }
  }
}
`;

export const DELETE_SLOT_QUERY = `
mutation($slotId: ID!) {
  deleteSlot(
    input: {
      slotId: $slotId
    }
  ) {
    clientMutationId
  }
}
`;

export class SlotService extends BaseGQLService {
  private readonly mapper = new SlotMapper();

  async getSlot(slotId: number): Promise<Slot> {
    return this.query(
      GET_SLOT_QUERY,
      {
        slotId: this.gqlIdEncoder.encode(ModelType.Slot, slotId),
      },
      (data) => this.mapper.mapGQLModel(data.slot)
    );
  }

  async getProjectSlots(projectId: number): Promise<Slot[]> {
    return this.query(
      GET_PROJECT_SLOTS_QUERY,
      {
        projectId: this.gqlIdEncoder.encode(ModelType.Project, projectId),
      },
      (data) => data.node.slots.edges.map((edge: any) => this.mapper.mapGQLModel(edge.node))
    );
  }

  async createSlot(projectId: number, name: string, description: string, traceType: string, tags: string[]): Promise<Slot> {
    return this.mutate(
      CREATE_SLOT_QUERY,
      {
        projectId: this.gqlIdEncoder.encode(ModelType.Project, projectId),
        traceType,
        name,
        description,
        tags,
      },
      (data) => this.mapper.mapGQLModel(data.createSlot.slot)
    );
  }

  async updateSlot(slotId: number, name: string, description: string, tags: string[]): Promise<Slot> {
    return this.mutate(
      UPDATE_SLOT_QUERY,
      {
        slotId: this.gqlIdEncoder.encode(ModelType.Slot, slotId),
        name,
        description,
        tags,
      },
      (data) => this.mapper.mapGQLModel(data.updateSlot.slot)
    );
  }

  async deleteSlot(slotId: number): Promise<void> {
    await this.mutate(DELETE_SLOT_QUERY, {
      slotId: this.gqlIdEncoder.encode(ModelType.Slot, slotId),
    });
  }

  async getSlotTypes(): Promise<SlotType[]> {
    return this.query(GET_SLOT_TYPES, {}, (data) => {
      const allTypes = data?.traceTypes || [];
      const allowedTypes = data?.viewer?.organization?.allowedTraceTypes || [];

      return allTypes.map((t: TraceType) => new SlotType(t, allowedTypes.includes(t)));
    });
  }
}
